import React from "react";
import { LegalLayout, H2, P, UL } from "./LegalLayout";

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout testid="privacy-page" title="Privacy Policy" lastUpdated="February 1, 2026">
      <P>
        This Privacy Policy explains how <strong>ULTRANEQ LLC</strong>, operating under the
        brand "Simple Style Mode" (referred to as "we", "us"), collects, uses and protects
        personal data when you visit simplestylemode.com or place an order with us. We are
        committed to protecting your privacy and handling your data lawfully and transparently.
      </P>

      <H2>1. Data Controller</H2>
      <UL>
        <li>Company: ULTRANEQ LLC (d/b/a "Simple Style Mode")</li>
        <li>Address: 2105 Vista Oeste NW, Albuquerque, NM 87120, United States</li>
        <li>Email: support@simplestylemode.com</li>
      </UL>

      <H2>2. Personal data we collect</H2>
      <P>Depending on your interaction with the Site, we may collect:</P>
      <UL>
        <li><strong>Order data:</strong> name, billing/shipping address, email address, phone number (optional), order history.</li>
        <li><strong>Payment data:</strong> card data is collected and processed directly by Stripe, Inc. We only store the last 4 digits and card brand for reference.</li>
        <li><strong>Contact data:</strong> name, email and message content when you use the contact form.</li>
        <li><strong>Technical data:</strong> IP address, browser type, device info, pages visited, via server logs and analytics cookies.</li>
        <li><strong>Marketing data:</strong> email address if you subscribe to our newsletter.</li>
      </UL>

      <H2>3. How we use your data</H2>
      <UL>
        <li>To process and deliver your order and related communication (legal basis: contract performance).</li>
        <li>To provide customer service and handle returns and refunds (contract performance).</li>
        <li>To comply with legal obligations, including tax and accounting (legal obligation).</li>
        <li>To improve our Site, prevent fraud and ensure security (legitimate interest).</li>
        <li>To send marketing communications where you have given consent (consent – you can opt out at any time).</li>
      </UL>

      <H2>4. Cookies</H2>
      <P>We use the following categories of cookies:</P>
      <UL>
        <li><strong>Essential cookies:</strong> required for the Site to function (cart, checkout session). These cannot be disabled.</li>
        <li><strong>Analytics cookies:</strong> help us understand how visitors use the Site so we can improve it (e.g., anonymised page-view counts).</li>
        <li><strong>Marketing cookies:</strong> used with your consent to measure the effectiveness of campaigns.</li>
      </UL>
      <P>
        You can manage cookie preferences through your browser settings or, where applicable,
        through our cookie banner. Refusing non-essential cookies will not affect your ability
        to browse or place orders.
      </P>

      <H2>5. Sharing with third parties</H2>
      <P>We share your personal data only with carefully selected service providers who help us operate the Site:</P>
      <UL>
        <li>Payment: Stripe, Inc. (USA) — PCI DSS compliant payment processor.</li>
        <li>Shipping: common carriers (USPS, UPS, FedEx, DHL) — for delivery.</li>
        <li>Hosting &amp; infrastructure: cloud providers located in the US and EU.</li>
        <li>Email delivery: transactional email providers for order confirmations.</li>
      </UL>
      <P>
        We never sell your personal data. Data is only shared with authorities when required by
        law.
      </P>

      <H2>6. International transfers</H2>
      <P>
        Where personal data is transferred outside of your country of residence, we ensure that
        adequate safeguards are in place, such as Standard Contractual Clauses approved by the
        European Commission.
      </P>

      <H2>7. Data retention</H2>
      <UL>
        <li>Order-related data: retained for the duration required by accounting and tax law (typically 7–10 years).</li>
        <li>Contact form messages: retained for 24 months for customer-service purposes.</li>
        <li>Newsletter data: retained until you unsubscribe.</li>
      </UL>

      <H2>8. Your rights</H2>
      <P>Subject to applicable law, you have the right to:</P>
      <UL>
        <li>Access the personal data we hold about you.</li>
        <li>Request correction of inaccurate data.</li>
        <li>Request deletion of your data ("right to be forgotten").</li>
        <li>Object to or restrict certain processing.</li>
        <li>Request portability of your data.</li>
        <li>Withdraw consent at any time (for processing based on consent).</li>
        <li>Lodge a complaint with your local data protection authority.</li>
      </UL>
      <P>
        To exercise any of these rights, email us at{" "}
        <a href="mailto:support@simplestylemode.com" className="underline">support@simplestylemode.com</a>.
        We will reply within 30 days.
      </P>

      <H2>9. Security</H2>
      <P>
        We implement industry-standard measures to protect your personal data, including HTTPS
        encryption, access controls, regular backups and vetted service providers. No online
        system is 100% secure; we therefore encourage you to use strong unique passwords and
        protect your devices.
      </P>

      <H2>10. Children</H2>
      <P>
        The Site is not directed at children under 16. We do not knowingly collect personal
        data from children. If you believe we have collected such data, please contact us so
        that we can delete it.
      </P>

      <H2>11. Changes to this policy</H2>
      <P>
        We may update this Privacy Policy occasionally. Any changes will be posted on this
        page with a new "Last updated" date.
      </P>

      <H2>12. Contact</H2>
      <P>
        Questions about this Privacy Policy can be sent to{" "}
        <a href="mailto:support@simplestylemode.com" className="underline">support@simplestylemode.com</a>.
      </P>
    </LegalLayout>
  );
}
